import { cloneElement, FC, useEffect, useContext, createContext } from 'react'
import { useLocation } from 'react-router-dom'
import { classes, style } from 'typestyle'

/* Core ========================================================================================= */
import config from '../../../../config'
import { withRouter } from '../../../contexts/with-router'

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'
import useBoolean from '../../../hooks/use-boolean'

/* Contexts ===================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Components =================================================================================== */
import Skeleton from '../../../components/skeleton'
import { Box } from '../../../components/box/box.component'
import { Circle } from '../../../components/circle'
import { Loader } from '../../../components/loader'
import { NavContainer } from './nav-container'
import { NavItem } from './nav-item'
import { NavItemWrapper, renderItems } from './render-nav-items'
import { NavTopContent, NavContent } from './nav-content'

/* Styles ======================================================================================= */
import { f16i } from '../../../styles/styleset/font-size/f16i'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { mr12 } from '../../../styles/styleset/margin/mr12'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { p8 } from '../../../styles/styleset/padding/p8'
import { pl12md } from '../../../styles/styleset/padding/pl12md'
import { pl16 } from '../../../styles/styleset/padding/pl16'
import { pl8 } from '../../../styles/styleset/padding/pl8'
import { pl8sm } from '../../../styles/styleset/padding/pl8sm'
import { pr12md } from '../../../styles/styleset/padding/pr12md'
import { pr16 } from '../../../styles/styleset/padding/pr16'
import { pr8 } from '../../../styles/styleset/padding/pr8'
import { pr8sm } from '../../../styles/styleset/padding/pr8sm'

const NAV_ITEM_HEIGHT = 36
let links

let _toggled: any = []
const ToggleContext = createContext<{ toggled: boolean[]; $toggled: any }>({
  toggled: _toggled,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  $toggled: () => {},
})

/* <Navbar /> =================================================================================== */
export const Navbar = withRouter((props: any) => {
  const location = useLocation()
  const { padForLogo = config.app.navbarProps.padForLogo } = props
  const { navigation } = useContext<any>(NavigationContext)

  links = navigation.payload.links

  const topLinks = (links || []).filter((a: any) => a.position === 'upper')
  const defaultLinks = (links || []).filter(
    (a: any) => a.position !== 'upper' && a.position !== 'bottom' && a.level === undefined,
  )
  const otherLinks = (links || []).filter((a: any) => a.level !== undefined)
  const [isScrolled, $isScrolled] = useValue(false)
  const [, $otherLinksToggled] = useValue(0)

  const [toggled, $toggled] = useValue(_toggled)

  return (
    <ToggleContext.Provider value={{ toggled, $toggled }}>
      <NavContainer setScroll={$isScrolled.set} context={props.context}>
        <NavTopContent shadow={isScrolled}>
          <Loader
            skeleton={
              <Box
                className={classes(pl8sm, pr8sm, pl12md, pr12md, pl16, pr16)}
                minHeight={35}
                alignCenterY
              >
                <Skeleton height={24} width={220} />
              </Box>
            }
            component={renderItems({
              links: topLinks,
              history: { location },
              position: 'upper',
              disableHighlight: true,
              isButton: true,
              padForLogo,
            })}
            timeout={100}
            loaded={topLinks.length > 0}
          />
        </NavTopContent>
        <NavContent>
          <Box
            className={style({ paddingBottom: otherLinks.length * 36 })}
            style={{
              overflow: toggled[0] ? 'hidden' : 'initial',
            }}
            onClick={() => {
              setTimeout(() => {
                _toggled = []
              }, 400)
            }}
          >
            <Loader
              skeleton={
                <Box>
                  <Box
                    className={classes(pl8sm, pr8sm, pl12md, pr12md, pl8, pr16)}
                    minHeight={35}
                    alignCenterY
                  >
                    <Circle className={mr12}>
                      <Skeleton height={22} width={22} variant={'circle'} />
                    </Circle>
                    <Skeleton height={20} width={120} />
                  </Box>
                  <Box
                    className={classes(pl8sm, pr8sm, pl12md, pr12md, pl8, pr16)}
                    minHeight={35}
                    alignCenterY
                  >
                    <Circle className={mr12}>
                      <Skeleton height={22} width={22} variant={'circle'} />
                    </Circle>
                    <Skeleton height={20} width={120} />
                  </Box>
                  <Box
                    className={classes(pl8sm, pr8sm, pl12md, pr12md, pl8, pr16)}
                    minHeight={35}
                    alignCenterY
                  >
                    <Circle className={mr12}>
                      <Skeleton height={22} width={22} variant={'circle'} />
                    </Circle>
                    <Skeleton height={20} width={120} />
                  </Box>
                </Box>
              }
              component={renderItems({
                links: defaultLinks,
                history: { location },
                padForLogo,
                breadcrumbs: navigation.payload.breadcrumbs,
              })}
              timeout={100}
              loaded={topLinks.length > 0}
            />
          </Box>
          {/* Stacked Navigation */}
          {otherLinks.length > 0 && (
            <NavSectionManager $otherLinksToggled={$otherLinksToggled}>
              {otherLinks.map((links: any, key: any) => {
                return (
                  <NavSection
                    defaultLinks={defaultLinks}
                    links={[links]}
                    key={key}
                    breadcrumbs={navigation.payload.breadcrumbs}
                    padForLogo={padForLogo}
                    index={key + 1}
                  />
                )
              })}
            </NavSectionManager>
          )}
        </NavContent>
      </NavContainer>
    </ToggleContext.Provider>
  )
})

let toggleTimeout: any = null
const NavSectionManager = (props: any) => {
  const { toggled, $toggled } = useContext(ToggleContext)

  const [loaded, $loaded] = useBoolean(false)
  const [toggleAnimated, $toggleAnimated] = useBoolean(_toggled[0])

  useEffect(() => {
    if (!loaded && _toggled[0] === undefined) {
      const newToggled =
        props.children.length > 0
          ? props.children.map(() => {
              return true
            })
          : _toggled

      $toggled.set(newToggled)

      props.$otherLinksToggled.set(newToggled.length)
      $loaded.toggle()
    }
  }, [props.children])

  useEffect(() => {
    clearTimeout(toggleTimeout)
    if (toggled[0]) {
      $toggleAnimated.set(toggled[0])
    } else {
      toggleTimeout = setTimeout(() => {
        $toggleAnimated.set(toggled[0])
      }, 400)
    }
  }, [toggled[0]])

  _toggled = toggled

  return (
    <Box
      baseline="surface"
      style={{
        position: toggleAnimated ? 'initial' : 'sticky',
        bottom: 0,
        // height: 40,
      }}
    >
      {props.children.map((child: any, key: number) => {
        const index = key
        const indexReversed = props.children.length - key - 1

        return cloneElement(child, {
          key,
          toggled: toggled?.[key] !== undefined ? toggled?.[key] : true,
          index,
          indexReversed,
          onClick: () => {
            $toggled.set((prev: any) => {
              const temp = [...prev]

              if (temp[key + 1] && temp[key]) {
                for (let i = key; i < temp.length; i++) {
                  temp[i] = !temp[i]
                }
              } else {
                if (!temp[key]) {
                  for (let i = 0; i < key + 1; i++) {
                    temp[i] = true
                  }
                } else {
                  temp[key] = !temp[key]
                }
              }

              props.$otherLinksToggled.set(temp.filter(a => a).length)

              return temp
            })
          },
        })
      })}
    </Box>
  )
}

const NavSection = (props: {
  links: any
  index?: number
  indexReversed?: number
  toggled?: boolean
  onClick?: any
  breadcrumbs?: any
  padForLogo?: boolean
  defaultLinks?: any
}) => {
  const { toggled, onClick, index = 0, indexReversed = 0, breadcrumbs, padForLogo } = props
  const location = useLocation()

  return (
    <Box baseline="surface">
      <Box
        baseline="surface"
        className={style({
          background: 'rgba(0,0,0,0.05)',
          height: toggled
            ? `calc(100% - ${NAV_ITEM_HEIGHT * index}px)`
            : NAV_ITEM_HEIGHT * (indexReversed + 1),
          bottom: 0,
          transition: '0.4s all ease',
          opacity: 1,
          $nest: {
            '.text': {
              opacity: 0,
              color: 'white',
            },
            '&:hover': {
              background: 'rgba(0,0,0,0)',
              cursor: 'pointer',
              $nest: {
                '.text': {
                  color: 'white',
                  opacity: 1,
                },
              },
            },
          },
        })}
        onClick={onClick}
        absolute
        fullWidth
      >
        <Box relative>
          <NavItemWrapper>
            <NavItem label={'Show Main Menu'} icon={'menu'} padForLogo={padForLogo} />
          </NavItemWrapper>
          <Box
            className={style({
              background: 'rgba(0,0,0, 0.1)',
              transition: '0.4s ease',
              height: `calc(100% - ${NAV_ITEM_HEIGHT * index}px)`,
              bottom: 0,
              opacity: 1,
              $nest: {
                '.text': {
                  opacity: 0,
                  color: 'white',
                },
                '&:hover': {
                  background: 'rgba(0,0,0,0)',
                  cursor: 'pointer',
                  $nest: {
                    '.text': {
                      color: 'white',
                      opacity: 1,
                    },
                  },
                },
              },
            })}
            absolute
            fullWidth
          >
            <Box
              absolute
              baseline="none"
              className={style({
                height: NAV_ITEM_HEIGHT,
                paddingRight: 8,
              })}
              alignCenterY
              alignRight
              fullWidth
            >
              <Circle size="xs" shadow>
                <i className={classes('material-icons', f16i)}>keyboard_arrow_down</i>
              </Circle>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        baseline="surface"
        style={{
          height: toggled
            ? `calc(100% - ${NAV_ITEM_HEIGHT + NAV_ITEM_HEIGHT * index}px)`
            : NAV_ITEM_HEIGHT * (indexReversed + 1),
        }}
        className={classes(
          style({
            bottom: 0,
            transition: '0.4s ease',
          }),
        )}
        absolute
        fullWidth
      >
        <Box
          baseline="surface"
          className={style({
            height: '100%',
            overflowY: toggled ? 'auto' : 'hidden',
            overflowX: 'hidden',
            $nest: {
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            },
          })}
        >
          {renderItems({
            links: props.links,
            history: { location },
            nested: true,
            disableHighlight: false,
            breadcrumbs,
            padForLogo,
          })}
          {!toggled && (
            <Box
              baseline="none"
              style={{
                transition: '0.4s ease',
              }}
              className={style({
                background: 'rgba(0,0,0,0.05)',
                height: '100%',
                bottom: 0,
                cursor: 'pointer',
                $nest: {
                  '.text': {
                    opacity: 0,
                    color: 'white',
                  },
                  '&:hover': {
                    background: 'rgba(0,0,0,0)',
                    cursor: 'pointer',
                    $nest: {
                      '.text': {
                        color: 'white',
                        opacity: 1,
                      },
                    },
                  },
                },
              })}
              onClick={onClick}
              absolute
              fullWidth
            >
              <Box
                baseline="none"
                className={style({ width: '100%', height: NAV_ITEM_HEIGHT })}
                alignCenterX
                alignCenterY
              >
                <Box
                  absolute
                  baseline="none"
                  className={style({
                    height: NAV_ITEM_HEIGHT,
                    paddingRight: 8,
                  })}
                  alignCenterY
                  alignRight
                  fullWidth
                >
                  <Circle size="xs" shadow>
                    <i className={classes('material-icons', f16i)}>keyboard_arrow_up</i>
                  </Circle>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const NavbarLoading: FC<{ context?: any }> = () => {
  return (
    <NavContainer context={null}>
      <NavTopContent>
        <Box minHeight={47} alignCenterY baseline="none">
          <Box className={p8} minHeight={24} minWidth={'100%'} baseline="none" column flex>
            <Box minWidth={36} className={mr8} baseline="none">
              <Skeleton width={24} height={24} variant="circle" />
            </Box>
            <Skeleton width={'60%'} height={24} />
          </Box>
        </Box>
      </NavTopContent>
      <NavContent>
        <Box minHeight={14} alignCenterY baseline="none" className={classes(pr8, pl8)}>
          <Skeleton width={'40%'} height={14} />
        </Box>

        <Box className={mb8} baseline="none">
          <Box minHeight={47} alignCenterY baseline="none">
            <Box className={p8} minHeight={24} minWidth={'100%'} baseline="none" column flex>
              <Box minWidth={36} className={mr8} baseline="none">
                <Skeleton width={24} height={24} variant="circle" />
              </Box>
              <Skeleton width={'60%'} height={24} />
            </Box>
          </Box>
          <Box minHeight={47} alignCenterY baseline="none">
            <Box className={p8} minHeight={24} minWidth={'100%'} baseline="none" column flex>
              <Box minWidth={36} className={mr8} baseline="none">
                <Skeleton width={24} height={24} variant="circle" />
              </Box>
              <Skeleton width={'60%'} height={24} />
            </Box>
          </Box>
          <Box minHeight={47} alignCenterY baseline="none">
            <Box className={p8} minHeight={24} minWidth={'100%'} baseline="none" column flex>
              <Box minWidth={36} className={mr8} baseline="none">
                <Skeleton width={24} height={24} variant="circle" />
              </Box>
              <Skeleton width={'60%'} height={24} />
            </Box>
          </Box>
        </Box>

        <Box minHeight={14} alignCenterY baseline="none" className={classes(pr8, pl8)}>
          <Skeleton width={'40%'} height={14} />
        </Box>
      </NavContent>
    </NavContainer>
  )
}
